import { useEffect, useRef, useState } from 'react';
import { OrganizationsApi } from '@/api/clients/organizations/organizations.api';

const DEBOUNCE_MS = 500;

export const useValidateCheckSequence = ({
  organizationId,
  checkSequenceEnabled,
  startingCheckNumber,
  skip = false,
}) => {
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestIdRef = useRef(0);
  const previousEnabledRef = useRef(checkSequenceEnabled);

  useEffect(() => {
    const toggleChanged = previousEnabledRef.current !== checkSequenceEnabled;
    previousEnabledRef.current = checkSequenceEnabled;

    if (!organizationId || skip) {
      requestIdRef.current += 1;
      setVerdict(null);
      setFailed(false);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    const validate = async () => {
      try {
        const { data } = await OrganizationsApi.validateCheckSequence(
          organizationId,
          {
            checkSequenceEnabled,
            startingCheckNumber,
          },
        );
        if (requestId !== requestIdRef.current) return;
        setVerdict(data);
        setFailed(false);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setVerdict(null);
        setFailed(true);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    };

    const timer = setTimeout(validate, toggleChanged ? 0 : DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [organizationId, checkSequenceEnabled, startingCheckNumber, skip]);

  return { verdict, loading, failed };
};
